/**
 * 매장폰에 사람이 등록한 번호와 APK가 실제 회선에서 읽어 보고한 번호를 비교한다.
 *
 * 한국 번호는 기기/통신사에 따라 `+82 10...` 또는 `010...`으로 오므로 국내형 숫자로
 * 맞춘 뒤 비교한다. 폰이 번호를 보고하지 못한 경우에만 대표가 직접 확인한 현재 등록 번호를 쓴다.
 */
export type StorePhoneLineStatus =
  | "match"
  | "manually_verified"
  | "mismatch"
  | "unknown";

export function canonicalStorePhone(value: string | null | undefined): string {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (digits.startsWith("82") && digits.length >= 11) return `0${digits.slice(2)}`;
  return digits;
}

export function storePhoneLineStatus(
  registeredPhoneNumber: string | null | undefined,
  reportedPhoneNumber: string | null | undefined,
  manuallyVerifiedPhoneNumber?: string | null,
): StorePhoneLineStatus {
  const registered = canonicalStorePhone(registeredPhoneNumber);
  const reported = canonicalStorePhone(reportedPhoneNumber);
  const manuallyVerified = canonicalStorePhone(manuallyVerifiedPhoneNumber);
  if (!registered) return "unknown";
  // 폰이 실제 번호를 알려주기 시작하면 대표 확인보다 실제 회선 대조가 항상 우선한다.
  if (reported) return registered === reported ? "match" : "mismatch";
  return manuallyVerified === registered ? "manually_verified" : "unknown";
}

/**
 * 현재 선택한 발송폰이 실제로 쓸 수 있다고 확인된 회선 번호를 돌려준다.
 *
 * 답장 화면은 이 번호를 원래 문자를 받은 번호와 따로 비교한다. 회선 확인 판정에
 * 원래 수신폰 번호를 섞으면, 정상인 다른 폰을 고른 순간 `unknown`으로 오판한다.
 */
export function verifiedStorePhoneLineNumber(
  registeredPhoneNumber: string | null | undefined,
  reportedPhoneNumber: string | null | undefined,
  manuallyVerifiedPhoneNumber?: string | null,
): string | null {
  const status = storePhoneLineStatus(
    registeredPhoneNumber,
    reportedPhoneNumber,
    manuallyVerifiedPhoneNumber,
  );
  if (status === "match") return canonicalStorePhone(reportedPhoneNumber);
  if (status === "manually_verified") return canonicalStorePhone(registeredPhoneNumber);
  return null;
}

/** 정상 확인된 선택폰이 원래 문자를 받은 번호와 다른 회선으로 답장하는지 판정한다. */
export function replyUsesDifferentStorePhoneLine(
  receivingPhoneNumber: string | null | undefined,
  selectedRegisteredPhoneNumber: string | null | undefined,
  selectedReportedPhoneNumber: string | null | undefined,
  selectedManuallyVerifiedPhoneNumber?: string | null,
): boolean {
  const receiving = canonicalStorePhone(receivingPhoneNumber);
  const sending = verifiedStorePhoneLineNumber(
    selectedRegisteredPhoneNumber,
    selectedReportedPhoneNumber,
    selectedManuallyVerifiedPhoneNumber,
  );
  return Boolean(receiving && sending && receiving !== sending);
}

export function maskStorePhone(value: string | null | undefined): string {
  const digits = canonicalStorePhone(value);
  if (digits.length === 11) return `${digits.slice(0, 3)}-****-****`;
  if (digits.length === 10) return `${digits.slice(0, 3)}-***-****`;
  return digits || "확인 안 됨";
}
